import { Drawer, FocusTrap, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import { EmployedsMobileFilterCheckbox } from '../EmployedsFiltersMobile/EmployedsMobileFilterCheckbox';
import { ReactComponent as IconClose } from 'assets/img/close.svg';
import { ReactComponent as IconSearch } from '../EmployedsFiltersMobile/img/iconSearch.svg';
import { ReactComponent as IconPlus } from './img/iconPlus.svg';
import filterStyles from '../EmployedsFiltersMobile/EmployedsMobileFilterDrawer.module.scss';
import styles from './CategoryOptionsList.module.scss';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import type { CategoryType } from 'redux/Api/employedsApi/type';
import { useDispatch, useSelector } from 'react-redux';
import { updateEmployedCategories } from 'redux/employeds/slice';
import { RootState } from 'redux/store';
import { mergeSelectedCategoriesForStore } from 'utils/mergeSelectedCategoriesForStore';

interface EmployedsCategoryDrawerProps {
  opened: boolean;
  onClose: () => void;
  selectedCategories: string[];
  employedId?: string;
  onApply?: (categories: string[]) => void;
  setIsCreatedModalOpen: (value: boolean) => void;
  fallbackCategories?: CategoryType[];
}

export const EmployedsCategoryDrawer: React.FC<EmployedsCategoryDrawerProps> = ({
  opened,
  onClose,
  selectedCategories,
  employedId,
  onApply,
  setIsCreatedModalOpen,
  fallbackCategories,
}) => {
  const dispatch = useDispatch();
  const categoriesFromStore = useSelector((state: RootState) =>
    employedId
      ? state.employeds.items.find((item) => item.id === Number(employedId))?.categories
      : undefined,
  );
  const previousCategories = categoriesFromStore ?? fallbackCategories;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery.trim(), 300);
  const [categoryPage, setCategoryPage] = useState(1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);

  useEffect(() => {
    if (!opened) return;
    setCategoryPage(1);
  }, [debouncedSearch, opened]);

  const { data: categoriesQueryData, isFetching: isFetchingCategories } =
    useGetCategoryEmployedsQuery(
      {
        page: categoryPage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      },
      { skip: !opened },
    );

  const categoriesList = categoriesQueryData?.data ?? [];
  const hasNextCategoryPage =
    !!categoriesQueryData?.meta &&
    categoriesQueryData.meta.current_page < categoriesQueryData.meta.last_page;

  const [localSelected, setLocalSelected] = useState<string[]>(() =>
    selectedCategories.map(String),
  );

  useEffect(() => {
    if (opened) {
      setLocalSelected(selectedCategories.map(String));
    }
  }, [opened, selectedCategories]);

  useEffect(() => {
    if (!isFetchingCategories) {
      loadMoreLockRef.current = false;
    }
  }, [isFetchingCategories]);

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategories || !hasNextCategoryPage || loadMoreLockRef.current) {
      return;
    }
    loadMoreLockRef.current = true;
    setCategoryPage((p) => p + 1);
  }, [isFetchingCategories, hasNextCategoryPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gapToBottom < 72 && el.scrollTop > 12) {
      loadMoreCategories();
    }
  }, [loadMoreCategories]);

  const options = useMemo(() => {
    return categoriesList
      .filter((option) => option.name != null && String(option.id).trim() !== '')
      .map((option) => ({
        id: option.id,
        label: option.name,
      }))
      .filter(
        (option) =>
          option.label !== null &&
          option.label !== undefined &&
          option.label.trim() !== '',
      );
  }, [categoriesList]);

  useLayoutEffect(() => {
    if (!opened) return;
    const el = scrollRef.current;
    if (!el) return;
  }, [opened, options.length, hasNextCategoryPage, isFetchingCategories]);

  const handleOptionChange = (optionId: string | number, checked: boolean) => {
    const id = String(optionId);
    setLocalSelected((prev) => {
      const normalized = prev.map(String);
      if (checked) {
        return normalized.includes(id) ? normalized : [...normalized, id];
      }
      return normalized.filter((x) => x !== id);
    });
  };

  const handleReset = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    const selectedIds = localSelected.map(String);

    if (employedId) {
      const merged = mergeSelectedCategoriesForStore(
        selectedIds,
        categoriesList,
        previousCategories,
      );
      dispatch(
        updateEmployedCategories({
          employedId: Number(employedId),
          categories: merged,
        }),
      );
    }

    onApply?.(selectedIds);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleNewCategory = () => {
    setIsCreatedModalOpen(true);
    handleClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position='bottom'
      zIndex={10000}
      withCloseButton={false}
      styles={{
        content: {
          height: 'fit-content',
          maxHeight: '90vh',
          borderRadius: '13px 13px 0px 0px',
          backgroundColor: '#FFF',
          padding: '16px 16px 4px 16px',
          boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflow: 'hidden',
        },
      }}>
      <FocusTrap.InitialFocus />
      <div className={filterStyles.header}>
        <span className={filterStyles.headerTitle}>Категории</span>
        <span className={filterStyles.headerClear} onClick={handleClose}>
          Готово
        </span>
      </div>

      <div className={filterStyles.searchWrapper}>
        <IconSearch className={filterStyles.searchIcon} />
        <input
          type='text'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder='Искать...'
          className={filterStyles.searchInput}
        />
        {searchQuery && (
          <IconClose className={filterStyles.closeIcon} onClick={handleClearSearch} />
        )}
      </div>

      <div className={styles.btnContainer}>
        <button className={styles.btn} onClick={handleNewCategory}>
          <IconPlus /> Новая категория
        </button>
      </div>

      <div
        ref={scrollRef}
        className={filterStyles.selectItemsNoScrollbar}
        onScroll={handleScroll}>
        {options.length === 0 && isFetchingCategories ? (
          <div className={styles.loadMoreRow}>
            <Loader size='xs' color='#002CFB' />
          </div>
        ) : options.length === 0 ? (
          <div className={filterStyles.noResults}>
            <span>Ничего не найдено</span>
          </div>
        ) : (
          <>
            {options.map((option) => {
              const idKey = String(option.id);
              const isChecked = localSelected.map(String).includes(idKey);
              return (
                <div
                  key={idKey}
                  className={filterStyles.selectItem}
                  onClick={() => handleOptionChange(option.id, !isChecked)}>
                  <span className={filterStyles.selectItemLabel}>{option.label}</span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <EmployedsMobileFilterCheckbox
                      checked={isChecked}
                      onChange={(checked) => handleOptionChange(option.id, checked)}
                    />
                  </div>
                </div>
              );
            })}
            {isFetchingCategories && hasNextCategoryPage && (
              <div className={styles.loadMoreRow}>
                <Loader size='xs' color='#002CFB' />
              </div>
            )}
          </>
        )}
      </div>

      <div className={filterStyles.actionsRow}>
        <FilterButton variant='reset' onClick={handleReset} disabled={false}>
          Сбросить
        </FilterButton>
        <FilterButton variant='apply' onClick={handleApply} disabled={false}>
          Сохранить
        </FilterButton>
      </div>
    </Drawer>
  );
};
